/**
 * Extended Warranty / RMA copy. The wording is fixed by the business (KAN-852)
 * and shared with safepointkiosk.com and moolahgaming.com — only the board name
 * changes per site, so keep edits in sync across the three projects.
 */

import { SITE } from "@/seo/site";

export const WARRANTY_BRAND = "NEMO LaunchPad";

/** Sender identity used in claim emails. */
export const WARRANTY_SITE = { name: SITE.name, url: SITE.url, phone: "(305) 697-3722" } as const;
export const BOARD_NAME = `${WARRANTY_BRAND} Computer Board`;

export const WARRANTY_PATHS = {
  overview: "/extended-warranty/",
  claim: "/extended-warranty/claim/",
  purchase: "/buy/?product=computer-board#order",
  contact: "/contact/",
} as const;

export const WARRANTY_SHIPPING_ADDRESS = {
  name: "Safe Point Kiosk",
  street: "401 East Jackson Street, Suite 2340",
  cityLine: "Tampa, FL 33602",
} as const;

export const WARRANTY_ADD_ON_LABEL = "Add Extended Warranty Protection to my computer board (+$300 per board)";

export const WARRANTY_SUPPORT_EMAIL = "support@safepointkiosk.com";

export const WARRANTY_PRICING = [
  { label: "Computer Board", price: "$500 Each", short: "$500" },
  { label: "Extended Warranty Protection", price: "$300 Per Board", short: "$300" },
  { label: "Total Package", price: "$800 Per Board", short: "$800" },
] as const;

export const warrantyContent = {
  title: `${WARRANTY_BRAND} Computer Boards & Extended Warranty Protection`,
  tagline: "ONE COMPUTER. MULTIPLE PLATFORMS. UNLIMITED FLEXIBILITY.",
  intro: `Our ${WARRANTY_BRAND} computer boards are designed for cross-platform compatibility, allowing customers to switch between supported gaming software platforms without purchasing new hardware.`,
  benefits: [
    {
      title: "FREE Unlimited Re-Imaging",
      description: "Switch between supported software platforms throughout your active warranty coverage.",
    },
    {
      title: "Cross-Platform Compatibility",
      description: "Use the same computer board for multiple compatible platforms.",
    },
    {
      title: "Professional Installation",
      description: "Our technicians re-image, configure, and test your computer.",
    },
    {
      title: "Easy Online Claims",
      description: "Submit your warranty claim directly through our website.",
    },
    {
      title: "Computer History Tracking",
      description: "Every re-image is registered and tracked by serial number.",
    },
  ],
  steps: [
    {
      title: "Submit Your Claim",
      paragraphs: [
        "Visit our website and select File a Warranty / Re-Imaging Claim.",
        "Complete the online claim form with your contact information, computer serial number, current platform, and requested new platform.",
      ],
    },
    {
      title: "Prepare Your Computer",
      bullets: [
        "Print and complete your Computer Exchange / Re-Imaging Form.",
        "Photograph your working computer with its matching cable secured.",
        `Email the photograph to ${WARRANTY_SUPPORT_EMAIL}.`,
        "Securely package the computer, matching cable, and completed form.",
      ],
    },
    {
      title: "Ship Your Computer",
      paragraphs: ["Mail your computer to:"],
      address: true,
      note: "Include your claim number inside the package.",
    },
    {
      title: "We Re-Image Your Computer",
      paragraphs: [
        "Once received, our technicians will verify your warranty, install your requested supported software platform, and test the computer.",
      ],
    },
    {
      title: "Return Shipping",
      paragraphs: ["After successful testing, your computer will be prepared for return shipping."],
    },
  ],
  important: [
    "Unlimited standard re-imaging is free while extended warranty coverage is active.",
    "Customers are responsible for shipping charges.",
    "Software platform compatibility is subject to hardware and licensing requirements.",
    "Additional hardware repairs and third-party licensing fees are not included in free re-imaging.",
    "Re-imaging may erase existing computer data and configurations.",
  ],
  closing: {
    headline: "STOP BUYING NEW COMPUTERS JUST TO CHANGE SOFTWARE!",
    pricing: [
      { label: "Computer Board", price: "$500" },
      { label: "Extended Warranty", price: "$300" },
      { label: "Complete Package", price: "$800" },
    ],
    body: "Purchase Extended Warranty Protection and enjoy the freedom to switch between supported platforms without additional re-imaging labor charges.",
  },
} as const;

export type WarrantyStep = (typeof warrantyContent.steps)[number];
